import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import type WaPagination from '@awesome.me/webawesome/dist/components/pagination/pagination.js';

import '@awesome.me/webawesome/dist/components/pagination/pagination.js';

interface PaginationProps {
  total: number;
  perPage?: number;
};

const Pagination: React.FC<PaginationProps> = ({ total, perPage = 12 }) => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { page?: number | string };
  const currentPage = Number(search.page) || 1;
  const paginationRef = useRef<WaPagination>(null);

  useEffect(() => {
    const pagination = paginationRef.current;
    if (!pagination) {
      return;
    }

    // Page links are rendered in the shadow DOM, so plain clicks are routed through the router instead of reloading the page.
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.composedPath().find((element): element is HTMLAnchorElement => element instanceof HTMLAnchorElement);
      if (!link?.href) {
        return;
      }

      const page = Number(new URL(link.href).searchParams.get('page'));
      if (!page) {
        return;
      }

      event.preventDefault();
      navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, page }),
      } as any);
    };

    pagination.addEventListener('click', handleClick);

    return () => pagination.removeEventListener('click', handleClick);
  }, [navigate, total, perPage]);

  if (total <= perPage) {
    return null;
  }

  return (
    <div className="pagination-container">
      <wa-pagination ref={(pagination) => { paginationRef.current = pagination; }} total={total} page-size={perPage} page={currentPage} href-template="?page={page}"></wa-pagination>
    </div>
  );
}

export default Pagination;
